/** Which Supabase auth events deserve a full data resync.
 *
 *  `onAuthStateChange` treated SIGNED_IN, TOKEN_REFRESHED and USER_UPDATED
 *  identically: rebind every realtime channel, then run the full `/database`
 *  convergence. A sign-in earns that. A token refresh does not — it is the
 *  hourly credential rotation for the session already open, and the
 *  convergence it triggered republished the whole local cache under whatever
 *  the user was doing. That is what "the screen keeps refreshing when you open
 *  a drawing" turned out to be (#927): a takeoff surface reloading roughly an
 *  hour in, with no action from the estimator and nothing in the console.
 *
 *  Nothing is lost by skipping it, because three other mechanisms already
 *  cover what it was there for:
 *
 *  - The rotated token reaches live channels without a teardown. supabase-js
 *    listens for auth events itself and calls `realtime.setAuth(token)` on
 *    TOKEN_REFRESHED (`SupabaseClient._handleTokenChanged`), which pushes an
 *    `access_token` message to every joined channel and updates the join
 *    payload for later rejoins (`RealtimeClient._performAuth`). Our own
 *    `restartRealtimeSubscriptions` called the same `setAuth` and then tore
 *    the channels down anyway.
 *  - DataService's own binding already declines to rebuild on a rotation:
 *    `bindRealtimeToAuth` handles INITIAL_SESSION, SIGNED_IN and SIGNED_OUT
 *    only, and no-ops unless the signed-in user id actually changed.
 *  - A tab that was asleep converges on waking regardless of auth, through
 *    `NetworkGateway.register('data-service-realtime', { onActivate })` →
 *    `refreshFromBackend()`. Auth's own refresh-on-wake path emits
 *    TOKEN_REFRESHED from the same `visibilitychange` that activates the
 *    gateway, so the convergence still happens — just not as a side effect of
 *    the credential rotation.
 *
 *  Deliberately conservative about what still resyncs. A native shell
 *  injecting a fresh token pair goes through `auth.setSession`, and auth-js
 *  emits SIGNED_IN for that, so a rescued session keeps its convergence. Any
 *  event this policy does not recognise resyncs too: a missed convergence
 *  shows stale data, which is the worse failure of the two.
 */

/** The auth events AuthContext's authenticated branch handles. */
export type AuthSyncEvent = 'SIGNED_IN' | 'TOKEN_REFRESHED' | 'USER_UPDATED';

export interface AuthSyncPlan {
  /** Re-read the profile row. Cheap, and `sameAuthUser` makes it publish
   *  nothing when the profile is unchanged, so it runs on every event. */
  hydrateUser: boolean;
  /** Rebind realtime channels and then run the full `/database` convergence —
   *  in that order, so the channels are armed before the slow fetch and no
   *  live change falls into the gap. */
  resyncData: boolean;
}

export function planAuthEventSync(event: AuthSyncEvent | string): AuthSyncPlan {
  return {
    hydrateUser: true,
    resyncData: event !== 'TOKEN_REFRESHED',
  };
}
